import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn, extractDomain } from "@/lib/utils";
import type { Page } from "@/schemas";
import { useState } from "react";

export interface PagePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  pages: Page[];
  onConfirm: (page: Page) => void;
  isConfirming?: boolean;
}

export function PagePickerDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  pages,
  onConfirm,
  isConfirming = false,
}: PagePickerDialogProps) {
  const [selectedPage, setSelectedPage] = useState<Page | null>(null);

  const handleOpenChange = (next: boolean) => {
    if (!next) setSelectedPage(null);
    onOpenChange(next);
  };

  const handleConfirm = () => {
    if (!selectedPage) return;
    const page = selectedPage;
    setSelectedPage(null);
    onConfirm(page);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {pages.length > 0 ? (
          <div className="max-h-64 overflow-y-auto rounded-md border border-border">
            {pages.map((page) => (
              <div
                key={page.id}
                className={cn(
                  "flex flex-col gap-0.5 px-3 py-2.5 cursor-pointer",
                  selectedPage?.id === page.id
                    ? "bg-primary/10"
                    : "hover:bg-muted/40",
                )}
                onClick={() => setSelectedPage(page)}
              >
                <span className="text-sm font-medium truncate">
                  {page.title ||
                    page.input_keyword ||
                    extractDomain(page.url!)}
                </span>
                <span className="text-xs text-muted-foreground truncate">
                  {page.url}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No published pages available for this domain. Add a page first.
          </p>
        )}

        <DialogFooter>
          <Button
            onClick={handleConfirm}
            disabled={!selectedPage || isConfirming}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
